/* Feb-2026 · Revenue Features admin panel.
 *
 * Master switch grid for the "slow release" revenue lifts. Each row
 * is a feature key from `revenue_features_registry.DEFAULT_FEATURES`
 * — flipping the switch persists via `PATCH /admin/revenue-features/{key}`.
 *
 * Design notes:
 *   • Grouped by category so ops can slow-release by cohort (retention
 *     flags first, then upsell, then monetization).
 *   • First release-time is stamped server-side so we can show the
 *     "New tools available" banner accurately on the user side.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, RefreshCw, Sparkles, TrendingUp, ShieldCheck, Users, Megaphone, Play, Pause } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_ICONS = {
  acquisition:   <Users className="h-3.5 w-3.5" />,
  upsell:        <Sparkles className="h-3.5 w-3.5" />,
  monetization:  <TrendingUp className="h-3.5 w-3.5" />,
  retention:     <ShieldCheck className="h-3.5 w-3.5" />,
};
const CATEGORY_ORDER = ['retention', 'acquisition', 'upsell', 'monetization'];
const IMPACT_TONE = { high: 'text-emerald-500', medium: 'text-amber-500', low: 'text-muted-foreground' };

export default function RevenueFeaturesPanel() {
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [savingKey, setSaving]  = useState(null);
  const [q, setQ]               = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.revenueFeatures.list();
      setRows(r?.features || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load revenue features.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (row, next) => {
    setSaving(row.key);
    try {
      const r = await adminApi.revenueFeatures.toggle(row.key, { enabled: next });
      setRows(cur => cur.map(x => (x.key === row.key ? { ...x, ...r.feature } : x)));
      toast.success(`${row.title} ${next ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Toggle failed.');
    } finally {
      setSaving(null);
    }
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(r =>
      (r.key || '').toLowerCase().includes(term) ||
      (r.title || '').toLowerCase().includes(term) ||
      (r.description || '').toLowerCase().includes(term),
    );
  }, [q, rows]);

  const grouped = useMemo(() => {
    const g = {};
    for (const r of filtered) {
      const cat = r.category || 'other';
      if (!g[cat]) g[cat] = [];
      g[cat].push(r);
    }
    return g;
  }, [filtered]);

  const enabledCount = rows.filter(r => r.enabled).length;

  // Feb-2026 · Zernio publishing is the flag admins reach for most often
  // (cross-platform social publishing kill-switch), so pin a big
  // Start/Stop card at the top of the panel. Falls back gracefully
  // while the flag list is still loading.
  const zernio = rows.find(r => r.key === 'zernio_publishing');
  const zernioOn = !!zernio?.enabled;
  const zernioBusy = savingKey === 'zernio_publishing';

  return (
    <div className="space-y-4" data-testid="admin-revenue-features-panel">
      {zernio && (
        <Card
          className={`border-2 ${zernioOn ? 'border-emerald-400 bg-emerald-50/40' : 'border-rose-400 bg-rose-50/40'}`}
          data-testid="admin-zernio-start-stop-card"
        >
          <CardContent className="flex flex-wrap items-center gap-3 p-4">
            <div className={`flex h-10 w-10 items-center justify-center rounded-full ${zernioOn ? 'bg-emerald-500/20 text-emerald-700' : 'bg-rose-500/20 text-rose-700'}`}>
              <Megaphone className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2">
                <div className="text-[13.5px] font-semibold text-foreground">Zernio Publishing</div>
                <span
                  className={`rounded-full px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wider ${zernioOn ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'}`}
                  data-testid="admin-zernio-state-badge"
                >
                  {zernioOn ? 'Running' : 'Stopped'}
                </span>
              </div>
              <p className="mt-1 text-[11.5px] text-muted-foreground">
                Master switch for cross-platform social publishing. When stopped, the editor's
                <b className="mx-1">Publish Everywhere</b> button is hidden and
                <code className="mx-1 rounded bg-muted px-1 font-mono-stat text-[10.5px]">POST /api/zernio/posts</code>
                returns 403. Webhooks &amp; analytics keep ingesting so historical data stays fresh.
              </p>
            </div>
            <Button
              size="sm"
              variant={zernioOn ? 'destructive' : 'default'}
              disabled={zernioBusy}
              onClick={() => toggle(zernio, !zernioOn)}
              className="h-9 gap-1.5 px-4 text-[12.5px] font-semibold"
              data-testid="admin-zernio-start-stop-btn"
            >
              {zernioBusy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : zernioOn ? (
                <><Pause className="h-3.5 w-3.5" /> Stop Publishing</>
              ) : (
                <><Play className="h-3.5 w-3.5" /> Start Publishing</>
              )}
            </Button>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle className="text-[14px]">Revenue Features (Slow Release)</CardTitle>
            <p className="text-[11.5px] text-muted-foreground">
              Toggle bonus tools on for your writers as they come on-board.
              Each release stamps a timestamp so users see a
              <b className="mx-1">"New tools available"</b> banner in their app.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-muted-foreground" data-testid="admin-rf-enabled-count">
              <b className="font-mono-stat text-primary">{enabledCount}</b> / {rows.length} enabled
            </span>
            <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="admin-rf-refresh">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Input
            placeholder="Filter by key, title, or description…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="mb-3 h-8 text-[12px]"
            data-testid="admin-rf-filter"
          />

          {loading ? (
            <div className="py-8 text-center text-[12px] text-muted-foreground">
              <Loader2 className="mx-auto h-4 w-4 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-8 text-center text-[12px] text-muted-foreground" data-testid="admin-rf-empty">
              {q ? 'No features match your filter.' : 'No revenue features registered.'}
            </div>
          ) : (
            <div className="space-y-4">
              {CATEGORY_ORDER.filter(c => grouped[c]).map((cat) => (
                <div key={cat} data-testid={`admin-rf-group-${cat}`}>
                  <div className="mb-2 flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
                    {CATEGORY_ICONS[cat]} {cat}
                  </div>
                  <div className="divide-y divide-border rounded-md border border-border">
                    {grouped[cat].map((row) => (
                      <div key={row.key} className="flex items-start gap-3 p-3" data-testid={`admin-rf-row-${row.key}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <code className="font-mono-stat text-[11px] text-primary">{row.key}</code>
                            <span className={`text-[9.5px] uppercase tracking-wider ${IMPACT_TONE[row.impact] || IMPACT_TONE.low}`}>
                              {row.impact} impact
                            </span>
                            {row.released_at && (
                              <span className="text-[9.5px] uppercase tracking-wider text-muted-foreground">
                                since {new Date(row.released_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          <div className="mt-1 text-[12.5px] font-semibold text-foreground">{row.title}</div>
                          <div className="mt-1 text-[11.5px] text-muted-foreground">{row.description}</div>
                        </div>
                        <div className="shrink-0">
                          <Switch
                            checked={!!row.enabled}
                            disabled={savingKey === row.key}
                            onCheckedChange={(v) => toggle(row, v)}
                            data-testid={`admin-rf-toggle-${row.key}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
